import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser } from '../../store/slices/authSlice';
import { GoogleLogin } from '@react-oauth/google';
import api from '../../services/api';
import { storeSession, getDeviceId, getDeviceName } from '../../utils/sessionKeys';
import { getValidRedirect } from '../../utils/redirectUtils';

const Login = () => {
  const { loading, error: authError, setAuthUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector(state => state.auth);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [formError, setFormError] = useState('');

  /**
   * Determines where to navigate after successful login.
   * Prefers ?redirect= query param (for soft-auth-gate return flow),
   * otherwise falls back to role-based defaults.
   */
  const getPostLoginPath = (role) => {
    const redirect = getValidRedirect(searchParams);
    if (redirect) return redirect;
    if (role === 'admin') return '/admin/invitations';
    if (role === 'astrologer') return '/astrologer/dashboard';
    return '/';
  };

  // Redirect if already logged in
  useEffect(() => {
    if (!isAuthenticated) return;
    navigate(getPostLoginPath(user?.role), { replace: true });
  }, [isAuthenticated, user?.role, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      setFormError('Please fill in all fields');
      return;
    }

    try {
      const response = await api.post('/login', {
        email: formData.email,
        password: formData.password
      }, {
        headers: {
          'X-Device-ID': getDeviceId(),
          'X-Device-Name': getDeviceName(),
        }
      });

      if (!response.data) {
        setFormError('Invalid credentials: Invalid email or password');
        return;
      }

      const data = response.data;

      // Extract role from JWT token
      let enhancedUser;
      try {
        const tokenParts = data.token.split('.');
        if (tokenParts.length !== 3) {
          throw new Error('Invalid token format');
        }

        const decodedPayload = JSON.parse(atob(tokenParts[1]));

        if (!decodedPayload.role) {
          throw new Error('Role not found in token');
        }

        enhancedUser = {
          ...data.user,
          role: decodedPayload.role
        };
      } catch (error) {
        enhancedUser = { ...data.user };
      }

      // Tokens are in httpOnly cookies (set by backend response). Store only user metadata.
      storeSession(enhancedUser.role || 'user', enhancedUser);

      dispatch(loginUser.fulfilled({
        user: enhancedUser
      }, 'manual-login', null));

      setAuthUser(enhancedUser, data.token);

      navigate(getPostLoginPath(enhancedUser.role), { replace: true });
    } catch (err) {
      if (err.message === 'Network failure' || err.message.includes('network') || !navigator.onLine) {
        setFormError('Network failure');
      } else {
        setFormError(err.response?.data?.error || 'Invalid credentials: Invalid email or password');
      }
      console.error('Login error:', err);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const response = await api.post('/auth/google', {
        credential: credentialResponse.credential,
      }, {
        headers: {
          'X-Device-ID': getDeviceId(),
          'X-Device-Name': getDeviceName(),
        }
      });

      const data = response.data;
      const googleAccessToken = data.access_token || data.token;

      let enhancedUser = { ...data.user };
      try {
        const tokenParts = googleAccessToken.split('.');
        if (tokenParts.length === 3) {
          const decodedPayload = JSON.parse(atob(tokenParts[1]));
          enhancedUser.role = decodedPayload.role;
        }
      } catch (e) { /* fallback */ }

      // Tokens are in httpOnly cookies. Store only user metadata.
      storeSession(enhancedUser.role || 'user', enhancedUser);

      dispatch(loginUser.fulfilled({
        user: enhancedUser,
      }, 'google-login', null));

      setAuthUser(enhancedUser, data.token);

      navigate(getPostLoginPath(enhancedUser.role), { replace: true });
    } catch (err) {
      setFormError(err.response?.data?.error || 'Google login failed');
    }
  };

  return (
    <div className="auth-container">
      <h2>Login</h2>
      <form onSubmit={handleSubmit} className="auth-form" role="form">
        <div className="form-group">
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="password">Password *</label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
          />
        </div>
        {formError && <div className="error-message">{formError}</div>}
        {authError && <div className="error-message">{authError}</div>}
        <button type="submit" disabled={loading} className="auth-button">
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </form>

      <div className="auth-divider">
        <span>OR</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <GoogleLogin
          onSuccess={handleGoogleSuccess}
          onError={() => setFormError('Google login failed. Please try again.')}
          theme="outline"
          size="large"
          width="350"
          text="continue_with"
        />
      </div>

      <p className="auth-switch">
        Don't have an account? <a href="/register">Sign up here</a>
      </p>
    </div>
  );
};

export default Login;
